<template>
    <Toast />
    <div class="grid p-fluid">
        <div class="col-12 lg:col-8">
            <div class="card">
                <div class="flex align-items-center" style="margin-bottom: 0.5rem;">
                    <span class="signer-icon"><i class="pi pi-file-pdf"></i></span>
                    <div>
                        <h5 style="margin-bottom: 2px;">Firmador de documentos PDF</h5>
                        <small style="font-style: italic; color: gray;">Firme cualquier documento PDF con su firma electrónica y envíelo por correo</small>
                    </div>
                </div>

                <div class="section-divider"></div>

                <div class="field">
                    <label><b>1. Documento</b></label>
                    <div v-if="!file">
                        <FileUpload
                            ref="fileUpload"
                            mode="basic"
                            name="file"
                            :maxFileSize="10485760"
                            accept="application/pdf"
                            :auto="false"
                            chooseLabel="Seleccionar PDF"
                            @select="onFileSelect"
                            @clear="onFileClear"
                        />
                        <small style="color: gray;">Solo archivos PDF. Máximo 10MB.</small>
                    </div>
                    <div v-else class="selected-file">
                        <i class="pi pi-file-pdf" style="font-size: 1.5rem; color: #d32f2f; margin-right: 0.75rem;"></i>
                        <div style="flex: 1; min-width: 0;">
                            <div class="file-name">{{ file.name }}</div>
                            <small style="color: gray;">{{ formatFileSize(file.size) }}</small>
                        </div>
                        <Button icon="pi pi-times" class="p-button-rounded p-button-text p-button-secondary" style="width: 2.5rem;" @click="onFileClear" v-tooltip.top="'Quitar archivo'"/>
                    </div>
                </div>

                <div class="field" style="margin-top: 1.5rem;">
                    <label><b>2. Firma visible</b></label>
                    <div class="field-checkbox" style="margin-bottom: 0.75rem;">
                        <Checkbox id="visibleSignature" v-model="visibleSignature" :binary="true" />
                        <label for="visibleSignature">Estampar el sello de la firma sobre el documento</label>
                    </div>
                    <PdfStampPlacer v-if="file && visibleSignature" :file="file" :placement="stampPlacement" @onChange="onPlacementChange" />
                    <small v-else-if="!visibleSignature" style="color: gray;">El documento se firmará igual, pero sin sello a la vista.</small>
                    <small v-else style="color: gray;">Cargue un documento para elegir dónde va el sello.</small>
                </div>

                <div class="field" style="margin-top: 1.5rem;">
                    <label><b>3. Destinatarios</b></label>
                    <Chips
                        v-model="emails"
                        separator=","
                        :addOnBlur="true"
                        placeholder="Escriba un correo y presione Enter"
                        @keydown="onEmailKeydown"
                        @add="onEmailAdd"
                    />
                    <small style="color: gray;">El documento firmado se enviará a estos correos. Separe varios con Enter, espacio o coma.</small>
                </div>

                <div class="flex" style="margin-top: 1.5rem;">
                    <Button
                        v-if="signedFile"
                        label="Descargar PDF firmado"
                        icon="pi pi-download"
                        class="p-button-outlined mr-2 mb-2"
                        style="width: auto;"
                        @click="downloadSignedFile()"
                    />
                    <Button
                        label="Firmar y enviar"
                        icon="pi pi-pencil"
                        class="p-button-raised p-button-success mb-2"
                        style="width: auto; margin-left: auto;"
                        :loading="loading"
                        :disabled="!file || emails.length === 0"
                        @click="signDocument()"
                    />
                </div>
            </div>
        </div>

        <div class="col-12 lg:col-4">
            <div class="card">
                <h5 style="margin-bottom: 1rem;">¿Cómo funciona?</h5>
                <div class="how-step" v-for="step in steps" :key="step.title">
                    <span class="how-icon"><i :class="step.icon"></i></span>
                    <div>
                        <b>{{ step.title }}</b>
                        <p style="margin: 0.2rem 0rem 0rem 0rem; color: gray;">{{ step.detail }}</p>
                    </div>
                </div>
                <div class="section-divider"></div>
                <small style="font-style: italic; color: gray;">
                    <i class="pi pi-info-circle" style="margin-right: 0.3rem;"></i>
                    Se utilizará la firma electrónica (.p12) registrada en la sección de <b>Negocio</b>. Verifique que su certificado esté vigente.
                </small>
            </div>
        </div>
    </div>
</template>

<script>
import httpRequest from '../../service/httpRequest';
import PdfStampPlacer from './PdfStampPlacer.vue';

export default {
    components: { PdfStampPlacer },
    data() {
        return {
            file: null,
            emails: [],
            visibleSignature: true,
            // Donde soltó el usuario el sello: {page, x, y, width, height} en
            // fracciones del área visible de la página. null = ubicación por
            // defecto del backend (pie de la última página).
            stampPlacement: null,
            loading: false,
            signedFile: null,
            signedFilename: null,
            emailRegex: /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,7}$/,
            steps: [
                { icon: 'pi pi-upload', title: 'Cargue su documento', detail: 'Seleccione el archivo PDF que desea firmar.' },
                { icon: 'pi pi-map-marker', title: 'Ubique el sello', detail: 'Arrastre el recuadro hasta el espacio que pide el formulario.' },
                { icon: 'pi pi-shield', title: 'Firma electrónica', detail: 'El documento se firma digitalmente con su certificado registrado.' },
                { icon: 'pi pi-send', title: 'Envío por correo', detail: 'El PDF firmado se envía automáticamente a los destinatarios.' },
            ],
        }
    },
    mounted() {
        const userEmail = this.$store.getters.getUser.email;
        if (userEmail) this.emails = [userEmail];
    },
    methods: {
        onFileSelect(event) {
            const file = event.files[0];
            if (!file) return;
            if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
                this.$toast.add({severity: 'warn', summary: 'Archivo inválido', detail: 'Solo se permiten archivos PDF', life: 4000});
                return;
            }
            this.file = file;
            this.signedFile = null;
            this.signedFilename = null;
            this.stampPlacement = null;
        },
        onFileClear() {
            this.file = null;
            this.stampPlacement = null;
        },
        onPlacementChange(placement) {
            this.stampPlacement = placement;
        },
        onEmailKeydown(event) {
            // Chips solo separa con coma: agregamos Enter y espacio para no perder lo escrito
            if (event.key !== ' ' && event.key !== 'Enter') return;
            const input = event.target;
            if (!input.value || !input.value.trim()) return;
            event.preventDefault();
            this.emails = this.normalizeEmails([...this.emails, input.value]);
            input.value = '';
        },
        onEmailAdd() {
            this.emails = this.normalizeEmails(this.emails);
        },
        normalizeEmails(entries) {
            const parts = [];
            entries.forEach(entry => String(entry).split(/[\s,;]+/).forEach(part => part && parts.push(part)));
            const invalids = parts.filter(email => !this.emailRegex.test(email));
            if (invalids.length > 0) {
                this.$toast.add({severity: 'warn', summary: 'Correo inválido', detail: invalids.join(", ") + " no es un correo válido", life: 4000});
            }
            const valids = parts.filter(email => this.emailRegex.test(email));
            return valids.filter((email, index) => valids.indexOf(email) === index);
        },
        formatFileSize(bytes) {
            if (bytes < 1024) return bytes + " B";
            if (bytes < 1048576) return (bytes / 1024).toFixed(1) + " KB";
            return (bytes / 1048576).toFixed(1) + " MB";
        },
        async signDocument() {
            if (!this.file || this.emails.length === 0) return;
            this.loading = true;
            const form = new FormData();
            form.append('file', this.file);
            form.append('emails', JSON.stringify(this.emails));
            form.append('visible', this.visibleSignature ? 'true' : 'false');
            // Sin placement el backend conserva su ubicación histórica
            if (this.visibleSignature && this.stampPlacement) {
                form.append('stamp', JSON.stringify(this.stampPlacement));
            }

            const response = await httpRequest.signPdfDocument(form);
            this.loading = false;

            if (response.status === 200) {
                this.signedFile = response.data.file;
                this.signedFilename = response.data.filename;
                this.$toast.add({severity: 'success', summary: 'Documento firmado', detail: 'El documento fue firmado y enviado a: ' + response.data.sentTo.join(", "), life: 6000});
                this.file = null;
                this.stampPlacement = null;
            } else if (response.status === 602) {
                // El backend dice si expiró o si la contraseña no abre el .p12.
                const reason = response.error || 'Su firma electrónica no está registrada, es inválida o ha expirado.';
                this.$toast.add({severity: 'error', summary: 'Firma electrónica inválida', detail: reason + ' Revísela en la sección de Negocio.', life: 8000});
            } else if (response.status === 422) {
                this.$toast.add({severity: 'error', summary: 'Error', detail: response.error, life: 5000});
            } else {
                this.$toast.add({severity: 'error', summary: 'Error', detail: 'No se pudo firmar el documento. Intente nuevamente.', life: 5000});
            }
        },
        downloadSignedFile() {
            const byteString = atob(this.signedFile);
            const bytes = new Uint8Array(byteString.length);
            for (let i = 0; i < byteString.length; i++) {
                bytes[i] = byteString.charCodeAt(i);
            }
            const blob = new Blob([bytes], {type: 'application/pdf'});
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = this.signedFilename;
            link.click();
            URL.revokeObjectURL(link.href);
        },
    }
}
</script>

<style scoped>
.signer-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 3rem;
    height: 3rem;
    border-radius: 12px;
    background-color: #fbe9e7;
    margin-right: 1rem;
    flex-shrink: 0;
}
.signer-icon i {
    font-size: 1.5rem;
    color: #d32f2f;
}
.section-divider {
    width: 100%;
    height: 1px;
    background-color: lightgray;
    margin: 1rem 0rem 1.5rem 0rem;
}
.selected-file {
    display: flex;
    align-items: center;
    border-radius: 12px;
    background-color: #f9fafb;
    padding: 1rem;
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}
.file-name {
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.how-step {
    display: flex;
    align-items: flex-start;
    margin-bottom: 1.25rem;
}
.how-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 50%;
    background-color: #f9fafb;
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
    margin-right: 0.75rem;
    flex-shrink: 0;
}
.how-icon i {
    color: #689f38;
}
</style>
